import React, { useState } from 'react';
import { ProgramScheduleType, ProgramIntensity, ProgramDomain, DomainAnswers } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { getAllDomains, getDomainConfig } from '../config/domainConfigs';
import AIChatPersonalization from './AIChatPersonalization';

interface ProgramCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProgram: (data: {
    goal: string;
    duration: number;
    scheduleType: ProgramScheduleType;
    customDays?: number[];
    questsPerDay: number;
    intensity: ProgramIntensity;
    domain: ProgramDomain;
    domainAnswers: DomainAnswers;
    aiFollowUpAnswers?: { [key: string]: string };
    // Time availability
    preferredTimeStart?: string;
    preferredTimeEnd?: string;
    availableDays?: number[];
    // Legacy fields for backward compatibility
    skillLevel?: 'beginner' | 'intermediate' | 'advanced';
    timePerDay?: number;
    learningStyle?: string;
    specificFocus?: string;
    availableResources?: string;
  }) => void;
}

const ProgramCreationModal: React.FC<ProgramCreationModalProps> = ({ isOpen, onClose, onCreateProgram }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0); // Start at 0 for domain selection
  const [isCreatingProgram, setIsCreatingProgram] = useState(false);

  // Domain Selection (Step 0 - NEW)
  const [selectedDomain, setSelectedDomain] = useState<ProgramDomain | null>(null);
  const [domainAnswers, setDomainAnswers] = useState<DomainAnswers>({});
  
  // Basic Program Info (Step 1)
  const [goal, setGoal] = useState('');
  const [duration, setDuration] = useState(90); // 3 months default
  
  // Schedule Settings (Step 2)
  const [scheduleType, setScheduleType] = useState<ProgramScheduleType>('daily');
  const [customDays, setCustomDays] = useState<number[]>([1, 3, 5]); // Mon, Wed, Fri
  const [questsPerDay, setQuestsPerDay] = useState(1); // 1 quest per training day
  const [intensity, setIntensity] = useState<ProgramIntensity>('moderate');

  // Time Availability (Step 2.5 - NEW)
  const [preferredTimeStart, setPreferredTimeStart] = useState('09:00');
  const [preferredTimeEnd, setPreferredTimeEnd] = useState('10:00');
  const [availableDays, setAvailableDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]); // All days by default (Sunday through Saturday)
  
  // Domain-Specific Questions (Step 3 - NEW)
  // Answers stored in domainAnswers state for static domains
  // For custom domain, AI chat answers stored in aiFollowUpAnswers
  const [aiFollowUpAnswers, setAiFollowUpAnswers] = useState<{ [key: string]: string } | undefined>(undefined);
  
  // Legacy Personalization (for backward compatibility)
  const [skillLevel, setSkillLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [timePerDay, setTimePerDay] = useState(30); // minutes
  const [learningStyle, setLearningStyle] = useState('');
  const [specificFocus, setSpecificFocus] = useState('');
  const [availableResources, setAvailableResources] = useState('');

  if (!isOpen) return null;

  // Show immediate loading state when program creation starts
  if (isCreatingProgram) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
        <div className="relative max-w-2xl w-full my-4 sm:my-8">
          <div className="bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 border border-slate-500/40 rounded-2xl shadow-2xl backdrop-blur-xl p-8 text-center">
            <div className="inline-block mb-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-600/80 to-cyan-600/80 flex items-center justify-center border border-blue-500/50 shadow-lg shadow-blue-500/30">
                <span className="text-3xl sm:text-4xl">🚀</span>
              </div>
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-orbitron text-white tracking-wider mb-4 uppercase">
              Starting Program Creation
            </h2>
            <p className="text-gray-400 font-rajdhani text-base sm:text-lg">
              Initializing AI program generation...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!goal.trim() || !selectedDomain) return;

    // Show loading immediately
    setIsCreatingProgram(true);

    onCreateProgram({
      goal: goal.trim(),
      duration,
      scheduleType,
      customDays: scheduleType === 'custom' ? customDays : undefined,
      questsPerDay,
      intensity,
      domain: selectedDomain,
      domainAnswers,
      aiFollowUpAnswers, // AI chat answers (for custom domain)
      preferredTimeStart,
      preferredTimeEnd,
      availableDays,
      skillLevel,
      timePerDay,
      learningStyle: learningStyle.trim() || undefined,
      specificFocus: specificFocus.trim() || undefined,
      availableResources: availableResources.trim() || undefined,
    });

    // Reset form
    resetForm();
  };

  const resetForm = () => {
    setStep(0);
    setSelectedDomain(null);
    setDomainAnswers({});
    setAiFollowUpAnswers(undefined);
    setGoal('');
    setDuration(90);
    setScheduleType('daily');
    setCustomDays([1, 3, 5]);
    setQuestsPerDay(1);
    setIntensity('moderate');
    setPreferredTimeStart('09:00');
    setPreferredTimeEnd('10:00');
    setAvailableDays([1, 2, 3, 4, 5, 6, 0]);
    setSkillLevel('beginner');
    setTimePerDay(30);
    setLearningStyle('');
    setSpecificFocus('');
    setAvailableResources('');
    setIsCreatingProgram(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleCustomDay = (day: number) => {
    setCustomDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    );
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getIntensityDescription = (level: ProgramIntensity) => {
    switch (level) {
      case 'light':
        return '15-20 min/day - Gentle pace';
      case 'moderate':
        return '30-45 min/day - Steady progress';
      case 'intense':
        return '60-90 min/day - Rapid advancement';
    }
  };

  const MAX_DURATION_DAYS = 180; // 6 months max
  
  const canProceedStep0 = selectedDomain !== null;
  const canProceedStep1 = goal.trim().length > 3 && duration >= 7 && duration <= MAX_DURATION_DAYS;
  const canProceedStep2 = true; // Time availability is optional
  const canProceedStep3 = selectedDomain ? true : false; // Domain questions are optional
  
  const totalSteps = 4;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="relative max-w-3xl w-full my-4 sm:my-8">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 border border-slate-500/40 rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="border-b border-slate-600/30 p-4 sm:p-6 bg-slate-800/50 backdrop-blur-sm">
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-blue-600/80 to-cyan-600/80 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <span className="text-2xl sm:text-3xl">🎯</span>
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-black font-orbitron text-white tracking-wider">
                    {t.aiGenerator.programTitle}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
                    {t.common.step} {step + 1} {t.common.of} {totalSteps} - {t.aiGenerator.programSubtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-all hover:rotate-90 hover:scale-110 duration-300 flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="relative mt-3 sm:mt-4 flex gap-2">
              {[0, 1, 2, 3].map((s) => (
                <div 
                  key={s} 
                  className={`flex-1 h-2 rounded-full transition-all duration-500 ${
                    s <= step 
                      ? s === step 
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg shadow-cyan-500/50' 
                        : 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg shadow-blue-500/30' 
                      : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Step 0: Domain Selection */}
            {step === 0 && (
              <div className="space-y-4 sm:space-y-6 animate-fade-in">
                <div className="bg-slate-700/50 backdrop-blur-sm border border-slate-500/50 rounded-xl p-4 sm:p-5 mb-4 sm:mb-6">
                  <div className="relative flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎯</span>
                    <h3 className="font-orbitron text-base sm:text-lg font-bold text-white">Select Your Goal Domain</h3>
                  </div>
                  <p className="relative text-sm text-gray-400 font-rajdhani">
                    Choose the category that best fits your goal. This helps us personalize your program.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {getAllDomains().map((domain) => (
                    <button
                      key={domain.id}
                      onClick={() => setSelectedDomain(domain.id)}
                      className={`relative p-4 sm:p-5 rounded-xl border text-left transition-all group ${
                        selectedDomain === domain.id
                          ? 'border-blue-500/50 bg-slate-700/70 backdrop-blur-sm shadow-lg shadow-blue-500/30'
                          : 'border-slate-600/40 hover:border-blue-500/40 bg-slate-700/50 backdrop-blur-sm'
                      }`}
                    >
                      
                      <div className="relative flex items-start gap-2 sm:gap-3">
                        <div className={`text-3xl sm:text-4xl transform transition-all duration-300 ${
                          selectedDomain === domain.id ? 'scale-105' : 'group-hover:scale-105'
                        }`}>
                          {domain.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-orbitron font-bold mb-1 text-sm sm:text-base transition-all ${
                            selectedDomain === domain.id 
                              ? 'text-blue-400' 
                              : 'text-white'
                          }`}>
                            {domain.label}
                          </h4>
                          <p className={`text-xs font-rajdhani mb-2 ${
                            selectedDomain === domain.id ? 'text-gray-300' : 'text-gray-400'
                          }`}>
                            {domain.description}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {domain.exampleGoals.slice(0, 2).map((example, idx) => (
                              <span key={idx} className={`text-xs px-2 py-1 rounded font-rajdhani transition-all ${
                                selectedDomain === domain.id 
                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30' 
                                  : 'bg-slate-700/50 text-gray-400 border border-slate-600/30'
                              }`}>
                                {example}
                              </span>
                            ))}
                          </div>
                        </div>
                        {selectedDomain === domain.id && (
                          <div className="flex-shrink-0">
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
                              <span className="text-white text-sm font-bold">✓</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {!selectedDomain && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 sm:p-3 flex items-start gap-1.5 sm:gap-2">
                    <span className="text-yellow-400 text-base sm:text-lg flex-shrink-0">⚠️</span>
                    <p className="text-xs sm:text-sm text-yellow-400 font-rajdhani">
                      Please select a domain to continue.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Basic Goal & Duration */}
            {step === 1 && (
              <div className="space-y-4 sm:space-y-6 animate-fade-in">
                <div className="bg-slate-700/50 backdrop-blur-sm border border-slate-500/50 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🎯</span>
                    <h3 className="font-orbitron text-base sm:text-lg font-bold text-white">{t.aiGenerator.whatsYourGoal}</h3>
                  </div>
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder={t.aiGenerator.programGoalPlaceholder}
                    className="w-full px-4 py-3 bg-slate-600/30 backdrop-blur-xl border border-slate-400/40 rounded-xl focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/20 focus:outline-none text-white font-sans placeholder-gray-400 transition-all text-base backdrop-blur-sm"
                    maxLength={100}
                  />
                  <div className="text-xs text-gray-400 mt-2 font-rajdhani">
                    {goal.length}/100 {t.aiGenerator.characters}
                  </div>
                </div>

                <div className="bg-slate-700/50 backdrop-blur-sm border border-slate-500/50 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">⏱️</span>
                    <h3 className="font-orbitron text-base sm:text-lg font-bold text-white">{t.aiGenerator.programDuration}</h3>
                  </div>
                  <div className="space-y-4">
                    {/* Slider */}
                    <div className="relative">
                      <input
                        type="range"
                        min="7"
                        max={MAX_DURATION_DAYS}
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value))}
                        className="slider-modern w-full h-2 bg-slate-700/50 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${((duration - 7) / (MAX_DURATION_DAYS - 7)) * 100}%, rgba(148, 163, 184, 0.3) ${((duration - 7) / (MAX_DURATION_DAYS - 7)) * 100}%, rgba(148, 163, 184, 0.3) 100%)`
                        }}
                      />
                      <style dangerouslySetInnerHTML={{ __html: `
                        .slider-modern::-webkit-slider-thumb {
                          appearance: none;
                          width: 20px;
                          height: 20px;
                          background: linear-gradient(135deg, rgb(59, 130, 246), rgb(14, 165, 233));
                          border-radius: 50%;
                          border: 3px solid rgba(15, 23, 42, 0.8);
                          cursor: pointer;
                          box-shadow: 0 0 15px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.3), inset 0 0 10px rgba(59, 130, 246, 0.2);
                          transition: all 0.2s ease;
                        }
                        .slider-modern::-webkit-slider-thumb:hover {
                          box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.5);
                          transform: scale(1.15);
                        }
                        .slider-modern::-moz-range-thumb {
                          width: 20px;
                          height: 20px;
                          background: linear-gradient(135deg, rgb(59, 130, 246), rgb(14, 165, 233));
                          border-radius: 50%;
                          border: 3px solid rgba(15, 23, 42, 0.8);
                          cursor: pointer;
                          box-shadow: 0 0 15px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.3);
                          transition: all 0.2s ease;
                        }
                        .slider-modern::-moz-range-thumb:hover {
                          box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.5);
                          transform: scale(1.15);
                        }
                      `}} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-xs font-rajdhani">1 {t.aiGenerator.week}</span>
                      <div className="text-center">
                        <div className={`text-3xl font-black font-orbitron bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent ${duration > MAX_DURATION_DAYS ? 'from-red-400 to-red-500' : ''}`}>{duration}</div>
                        <div className="text-sm text-gray-400 font-rajdhani">
                          {t.programs.days} ({Math.ceil(duration / 7)} {t.aiGenerator.weeks})
                        </div>
                      </div>
                      <span className="text-gray-400 text-xs font-rajdhani">6 months</span>
                    </div>
                    {duration > MAX_DURATION_DAYS && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 sm:p-3 flex items-start gap-1.5 sm:gap-2">
                        <span className="text-red-400 text-base sm:text-lg flex-shrink-0">⚠️</span>
                        <div className="flex-1">
                          <div className="text-red-400 font-orbitron font-bold text-xs sm:text-sm mb-0.5 sm:mb-1">Duration Too Long</div>
                          <div className="text-gray-300 text-[10px] sm:text-xs font-rajdhani">
                            Programs are limited to {MAX_DURATION_DAYS} days (6 months) to prevent timeouts. 
                            For longer goals, create multiple shorter programs or break your goal into phases.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!canProceedStep1 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 sm:p-3 flex items-start gap-1.5 sm:gap-2">
                    <span className="text-yellow-400 text-base sm:text-lg flex-shrink-0">⚠️</span>
                    <p className="text-xs sm:text-sm text-yellow-400 font-rajdhani">
                      {t.aiGenerator.goalMin}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Time Availability & Intensity */}
            {step === 2 && (
              <div className="space-y-4 sm:space-y-6 animate-fade-in">
                {/* Time Availability */}
                <div className="bg-slate-700/50 backdrop-blur-sm border border-slate-500/50 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🕐</span>
                    <h3 className="font-orbitron text-sm font-bold text-white">Preferred Time & Days</h3>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-rajdhani text-gray-400 mb-1">Available Time Slot</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={preferredTimeStart}
                          onChange={(e) => setPreferredTimeStart(e.target.value)}
                          className="px-3 py-2 bg-slate-600/30 backdrop-blur-xl border border-slate-400/40 rounded-lg focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/20 focus:outline-none text-white font-sans text-sm"
                        />
                        <span className="text-gray-400">to</span>
                        <input
                          type="time"
                          value={preferredTimeEnd}
                          onChange={(e) => setPreferredTimeEnd(e.target.value)}
                          className="px-3 py-2 bg-slate-600/30 backdrop-blur-xl border border-slate-400/40 rounded-lg focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/20 focus:outline-none text-white font-sans text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-rajdhani">
                        When do you prefer to work on this program?
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-rajdhani text-gray-400 mb-2">Available Days</label>
                      <div className="grid grid-cols-7 gap-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setAvailableDays(prev =>
                                prev.includes(index)
                                  ? prev.filter(d => d !== index)
                                  : [...prev, index]
                              );
                            }}
                            className={`p-2 rounded-lg border font-orbitron font-bold text-xs transition-all ${
                              availableDays.includes(index)
                                ? 'border-blue-500/50 bg-slate-700/70 text-blue-400'
                                : 'border-slate-600/40 text-gray-500 hover:border-slate-500/60'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-rajdhani">
                        Which days work best for you?
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-700/50 backdrop-blur-sm border border-slate-500/50 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">⚡</span>
                    <h3 className="font-orbitron text-sm font-bold text-white">{t.aiGenerator.intensity}</h3>
                  </div>
                  <select
                    value={intensity}
                    onChange={(e) => setIntensity(e.target.value as ProgramIntensity)}
                    className="w-full px-4 py-3 bg-slate-600/30 backdrop-blur-xl border border-slate-400/40 rounded-xl focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/20 focus:outline-none text-white font-sans text-base"
                  >
                    <option value="light">{t.aiGenerator.intensityLight}</option>
                    <option value="moderate">{t.aiGenerator.intensityModerate}</option>
                    <option value="intense">{t.aiGenerator.intensityIntense}</option>
                  </select>
                  <div className="text-xs text-gray-400 mt-2 font-rajdhani">
                    {t.aiGenerator.intensityDesc[intensity]}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: AI Personalization Chat - ALL Domains */}
            {step === 3 && selectedDomain && (
              <div className="space-y-4 sm:space-y-6 animate-fade-in">
                <AIChatPersonalization
                  domain={selectedDomain}
                  goal={goal}
                  duration={duration}
                  scheduleType={scheduleType}
                  customDays={scheduleType === 'custom' ? customDays : undefined}
                  questsPerDay={questsPerDay}
                  intensity={intensity}
                  domainAnswers={domainAnswers}
                  preferredTimeStart={preferredTimeStart}
                  preferredTimeEnd={preferredTimeEnd}
                  availableDays={availableDays}
                  onComplete={(answers) => {
                    setAiFollowUpAnswers(answers);
                    handleSubmit();
                  }}
                  onSkip={() => {
                    // Skip AI chat and proceed with program creation
                    setAiFollowUpAnswers(undefined);
                    handleSubmit();
                  }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-600/30 p-4 sm:p-6 bg-slate-800/50 backdrop-blur-sm flex justify-between gap-2 sm:gap-3">
            {/* Back button - hide on step 3 (AI chat handles navigation) */}
            {step > 0 && step !== 3 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-slate-700/50 hover:bg-slate-600/70 border border-slate-600/50 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
              >
                ← {t.common.back}
              </button>
            )}
            
            {/* Next/Submit buttons - hide on step 3 (AI chat handles submission) */}
            {step !== 3 && (
              <>
                {step < 3 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={
                      (step === 0 && !canProceedStep0) ||
                      (step === 1 && !canProceedStep1) ||
                      (step === 2 && !canProceedStep2)
                    }
                    className={`ml-auto px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all relative overflow-hidden ${
                      (step === 0 && !canProceedStep0) ||
                      (step === 1 && !canProceedStep1) ||
                      (step === 2 && !canProceedStep2)
                        ? 'bg-slate-700/50 text-gray-500 cursor-not-allowed border border-slate-600/50'
                        : 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-500/50 hover:border-blue-400/70 hover:shadow-lg hover:shadow-blue-500/30 text-white transform hover:scale-[1.02]'
                    }`}
                  >
                    <span className="relative">{t.common.next} →</span>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="ml-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-500 border border-blue-400/50 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/50"
                  >
                    🚀 {t.aiGenerator.generateProgram}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCreationModal;
